import React from "react";
import { Opportunity } from "../types";

interface Props {
  opportunity: Opportunity;
  onClick: (opp: Opportunity) => void;
  showNewBadge?: boolean;
}

function OpportunityCard({ opportunity, onClick, showNewBadge = false }: Props) {
  const opp = opportunity;
  const score = opp.total_score ?? 0;
  const value = opp.estimated_value
    ? `$${Number(opp.estimated_value).toLocaleString()}`
    : "Not specified";

  const aiResult = opp.gate3_result || "N/A";
  const resultColor =
    aiResult === "QUALIFIED" ? "#27ae60"
    : aiResult === "MARGINAL" ? "#f39c12"
    : aiResult === "DISQUALIFIED" ? "#e74c3c"
    : "#95a5a6";

  const deadline = opp.response_deadline
    ? new Date(opp.response_deadline).toLocaleDateString("en-US")
    : "No deadline";

  // Gate rejection info — only populated for workflow-rejected items
  // Status values are set by n8n: "scored" = Gate 2 fail, "disqualified" = Gate 3 fail
  // Gate 1 rejects also have status "rejected" but are identified by gate1_result="reject"
  let gateLabel: string | null = null;
  let gateReason: string | null = null;
  if (opp.gate1_result === "reject") {
    gateLabel = "Gate 1";
    gateReason = opp.gate1_reject_reason || null;
  } else if (opp.status === "disqualified" || opp.gate3_result === "DISQUALIFIED") {
    gateLabel = "Gate 3";
    const r = opp.gate3_reasoning || null;
    gateReason = r ? (r.length > 120 ? r.slice(0, 120) + "…" : r) : null;
  } else if (opp.status === "scored") {
    gateLabel = "Gate 2";
    gateReason = opp.gate2_score !== null ? `Score ${opp.gate2_score}/100 (below threshold)` : null;
  }

  return (
    <div
      onClick={() => onClick(opp)}
      style={{
        backgroundColor: "white", borderRadius: "8px",
        border: "1px solid #e0e0e0", padding: "16px", cursor: "pointer",
        transition: "box-shadow 0.2s", marginBottom: "12px",
      }}
      onMouseEnter={(e) => (e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.12)")}
      onMouseLeave={(e) => (e.currentTarget.style.boxShadow = "none")}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div style={{ flex: 1, marginRight: "16px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
            <h3 style={{ margin: 0, fontSize: "15px", color: "#2c3e50", lineHeight: 1.3 }}>
              {opp.title || "Untitled Opportunity"}
            </h3>
            {showNewBadge && opp.status === "new" && (
              <span style={{
                display: "inline-flex", alignItems: "center", gap: "3px",
                backgroundColor: "#1a5276", color: "white",
                fontSize: "10px", fontWeight: 700, letterSpacing: "0.5px",
                padding: "2px 7px", borderRadius: "10px", textTransform: "uppercase",
                flexShrink: 0,
              }}>
                ★ New
              </span>
            )}
          </div>
          <div style={{ fontSize: "13px", color: "#666", lineHeight: 1.6 }}>
            <span>{opp.agency}</span>
            {opp.naics_code && <span> &middot; NAICS {opp.naics_code}</span>}
            <span> &middot; {value}</span>
            {opp.state && <span> &middot; {opp.state}</span>}
          </div>
          <div style={{ fontSize: "12px", color: "#999", marginTop: "4px" }}>
            Deadline: {deadline}
          </div>
          {gateLabel && (
            <div style={{ fontSize: "12px", color: "#c0392b", marginTop: "5px" }}>
              <span style={{ fontWeight: 700 }}>{gateLabel}</span>
              {gateReason && <span> · {gateReason}</span>}
            </div>
          )}
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{
            fontSize: "22px", fontWeight: 700,
            color: score >= 55 ? "#27ae60" : "#e67e22",
          }}>
            {score}
          </div>
          <div style={{ fontSize: "11px", color: "#999" }}>/ 100</div>
          <div style={{
            marginTop: "6px", fontSize: "11px", fontWeight: 600,
            color: resultColor, textTransform: "uppercase",
          }}>
            {aiResult}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OpportunityCard;
